<template>
  <div
    class="grid-column-image relative w-full overflow-hidden"
    :class="
      isFlexible
        ? 'h-full min-h-0 flex-1'
        : [aspectClass, 'h-full min-h-0 grow']
    "
  >
    <component
      :is="lightboxEnabled ? 'a' : 'div'"
      v-bind="lightboxAttrs"
      class="absolute inset-0 size-full"
      :class="lightboxEnabled && 'cursor-zoom-in'"
    >
      <MediaImage
        v-bind="image"
        :responsive-style="responsiveStyle"
        :aspect-ratio="aspectRatio"
        :crop-alignment="cropAlignment"
        fit="scale"
        image-class="h-full w-full object-cover"
        class="size-full"
        :no-draggable="lightboxEnabled"
      />
      <span
        v-if="lightboxEnabled && lightboxAttrs['data-title']"
        class="sr-only"
      >
        {{ lightboxAttrs['data-title'] }}
      </span>
    </component>
  </div>
</template>

<script lang="ts" setup>
import type { ParagraphImageFragment } from '#graphql-operations'
import type { ResponsiveStyleName } from '~/helpers/responsiveStyles'
import { getCantoImageStyle, resolveCantoCropAlignment } from '~/helpers/canto'
import { responsiveStylesMap } from '~/helpers/responsiveStyles'

type GridImageOrientation =
  'landscape' | 'portrait' | 'square' | 'wide' | 'flexible'

/** Landscape (4:3) at 1/4 column — fallback crop ratio for flexible cells. */
const UNIT_SPAN = 3
const UNIT_ASPECT = 4 / 3

const props = defineProps<ParagraphImageFragment>()

const { options } = defineBlokkli({
  bundle: 'image',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
  // This renders the component when the parent is a grid column.
  renderFor: {
    parentBundle: 'grid_column',
  },
  options: {
    orientation: {
      type: 'radios',
      label: 'Format',
      default: 'landscape',
      options: {
        landscape: '4:3',
        portrait: '3:4',
        square: '1:1',
        wide: '2:1',
        flexible: 'Flexible',
      },
    },
    cropAlignment: {
      type: 'radios',
      label: 'Fokus',
      default: 'mc',
      options: {
        tl: { label: 'Oben links', description: 'Oben links' },
        tc: { label: 'Oben mitte', description: 'Oben mitte' },
        tr: { label: 'Oben rechts', description: 'Oben rechts' },
        ml: { label: 'Mitte links', description: 'Mitte links' },
        mc: { label: 'Mitte', description: 'Mitte' },
        mr: { label: 'Mitte rechts', description: 'Mitte rechts' },
        bl: { label: 'Unten links', description: 'Unten links' },
        bc: { label: 'Unten mitte', description: 'Unten mitte' },
        br: { label: 'Unten rechts', description: 'Unten rechts' },
      },
    },
  },
})

const cropAlignment = computed(() =>
  resolveCantoCropAlignment(options.value.cropAlignment),
)

const orientation = computed<GridImageOrientation>(() => {
  const value = options.value.orientation as GridImageOrientation | undefined
  if (
    value === 'portrait' ||
    value === 'square' ||
    value === 'wide' ||
    value === 'flexible' ||
    value === 'landscape'
  ) {
    return value
  }
  return 'landscape'
})

const isFlexible = computed(() => orientation.value === 'flexible')

// Fixed formats use their ratio as a base height, then grow to fill a taller
// sibling column. Flexible has no intrinsic ratio — it only fills the height
// set by fixed siblings (so a half + two quarters stays aligned).
const ASPECT_CLASS: Record<
  Exclude<GridImageOrientation, 'flexible'>,
  string
> = {
  landscape: 'aspect-[4/3]',
  portrait: 'aspect-[3/4]',
  square: 'aspect-square',
  wide: 'aspect-[2/1]',
}

const ASPECT_RATIO: Record<
  Exclude<GridImageOrientation, 'flexible'>,
  number
> = {
  landscape: 4 / 3,
  portrait: 3 / 4,
  square: 1,
  wide: 2,
}

const aspectClass = computed(() => {
  if (orientation.value === 'flexible') return ''
  return ASPECT_CLASS[orientation.value]
})

const columnSpan = useParagraphContext('gridColumnSpan', 6)
const lightboxEnabled = useParagraphContext('gridLightbox', false)

// Fixed: exact format crop. Flexible: estimate matching a 1/4 @ 4:3 unit for
// the Canto source; layout height comes from the stretched column.
const aspectRatio = computed(() => {
  if (orientation.value === 'flexible') {
    return columnSpan.value / (UNIT_SPAN / UNIT_ASPECT)
  }
  return ASPECT_RATIO[orientation.value]
})

// Pick the source based on how wide the image is really rendered, so a small
// column doesn't download a full-width source.
const responsiveStyle = computed<ResponsiveStyleName>(() => {
  const span = columnSpan.value

  if (span >= 10) return 'column_full'
  if (span >= 5) return 'column_half'
  if (span >= 4) return 'column_third'
  if (span >= 3) return 'column_quarter'
  return 'column_fifth'
})

const lightboxAttrs = computed(() => {
  if (!lightboxEnabled.value || !props.image) {
    return {}
  }

  const image = props.image

  if (image.__typename === 'MediaImage') {
    return {
      href: image.fileUrl?.path || undefined,
      'data-pswp-width': image.image?.width || undefined,
      'data-pswp-height': image.image?.height || undefined,
      'data-title': image.image?.title || image.image?.alt || undefined,
      draggable: false,
      tabindex: -1 as const,
    }
  }

  if (image.__typename === 'MediaCanto') {
    // Must use renderUrl (same as CantoImage) — assetId is not a loadable URL.
    const imageStyle =
      responsiveStylesMap.get('hero_slider')?.styles?.[0]?.id ||
      '1920_1152_focal_scale_crop'
    const width = Number(imageStyle.split('_')[0]) || 1920
    const height = aspectRatio.value
      ? Math.round(width / aspectRatio.value)
      : Number(imageStyle.split('_')[1]) || 1152
    const canto = getCantoImageStyle(image.renderUrl || '', {
      width,
      height,
      orientation: cropAlignment.value,
    })
    return {
      href: canto.url,
      'data-pswp-width': canto.width,
      'data-pswp-height': canto.height,
      'data-title': image.titleCanto || image.altCanto || undefined,
      draggable: false,
      tabindex: -1 as const,
    }
  }

  return {}
})
</script>

<style>
@reference '~/assets/css/tailwind.css';
.grid-column-image .responsive-image {
  @apply h-full w-full;
}
</style>
